import { type ScopedThreadRef, type ThreadId } from "@cmmd-forge/contracts";
import { useCallback, useState } from "react";
import { sanitizeThreadErrorMessage } from "~/rpc/transportError";
import { shouldWriteThreadErrorToCurrentServerThread } from "../ChatView.logic";
import { type DraftId } from "../../draftId";
import { type Thread } from "../../types";

export function useChatThreadErrorState(input: {
  readonly draftId: DraftId | null;
  readonly routeThreadRef: ScopedThreadRef;
  readonly serverThread: Thread | undefined;
  readonly setStoreThreadError: (threadId: ThreadId, error: string | null) => void;
}): {
  readonly localDraftErrorsByDraftId: Record<string, string | null>;
  readonly setThreadError: (targetThreadId: ThreadId | null, error: string | null) => void;
} {
  const { draftId, routeThreadRef, serverThread, setStoreThreadError } = input;
  const [localDraftErrorsByDraftId, setLocalDraftErrorsByDraftId] = useState<
    Record<string, string | null>
  >({});

  const setThreadError = useCallback(
    (targetThreadId: ThreadId | null, error: string | null) => {
      if (!targetThreadId) return;
      const nextError = sanitizeThreadErrorMessage(error);
      const isCurrentServerThread = shouldWriteThreadErrorToCurrentServerThread({
        serverThread,
        routeThreadRef,
        targetThreadId,
      });
      if (isCurrentServerThread) {
        setStoreThreadError(targetThreadId, nextError);
        return;
      }
      const localDraftErrorKey = draftId ?? targetThreadId;
      setLocalDraftErrorsByDraftId((existing) => {
        if ((existing[localDraftErrorKey] ?? null) === nextError) {
          return existing;
        }
        return {
          ...existing,
          [localDraftErrorKey]: nextError,
        };
      });
    },
    [draftId, routeThreadRef, serverThread, setStoreThreadError],
  );

  return { localDraftErrorsByDraftId, setThreadError };
}
